import { memo, useState } from 'react';
import RangeIndicator from './RangeIndicator';
import MarketAlignmentAnalysis from './MarketAlignmentAnalysis';
import { 
  getCommunicationFrequencyLevel, 
  getCommunicationStyleLevel, 
  getStrategicInfluenceLevel, 
  getResultVisibilityLevel,
  LEVEL_LABELS 
} from '../utils/compensationPartner/levelMapper';

// Helper function to calculate career progression metrics
function calculateCareerProgressionMetrics(positions) {
  if (positions.length < 2) return null;
  
  const firstPosition = positions[0];
  const lastPosition = positions.at(-1);
  
  // Total progression
  const totalSalaryProgression = lastPosition.salary.peerigonCurrentBasic - firstPosition.salary.peerigonCurrentBasic;
  const totalSalaryProgressionPercent = ((lastPosition.salary.peerigonCurrentBasic / firstPosition.salary.peerigonCurrentBasic) - 1) * 100;
  
  // Market progression
  const totalMarketProgression = lastPosition.salary.medianBasic - firstPosition.salary.medianBasic;
  const totalMarketProgressionPercent = ((lastPosition.salary.medianBasic / firstPosition.salary.medianBasic) - 1) * 100;
  
  // Calculate individual jumps
  const salaryJumps = [];
  for (let i = 0; i < positions.length - 1; i++) {
    const current = positions[i];
    const next = positions[i + 1];
    const jumpPercent = ((next.salary.peerigonCurrentBasic / current.salary.peerigonCurrentBasic) - 1) * 100;
    const jumpEuro = next.salary.peerigonCurrentBasic - current.salary.peerigonCurrentBasic;
    
    salaryJumps.push({
      from: current.positionsBezeichnung,
      to: next.positionsBezeichnung,
      percentIncrease: jumpPercent,
      euroIncrease: jumpEuro
    });
  }
  
  // Find largest and smallest jumps
  const largestJump = salaryJumps.reduce((max, jump) => 
    jump.percentIncrease > max.percentIncrease ? jump : max, salaryJumps[0]);
  const smallestJump = salaryJumps.reduce((min, jump) => 
    jump.percentIncrease < min.percentIncrease ? jump : min, salaryJumps[0]);
  
  // Average increase per level
  const averageIncreasePercent = totalSalaryProgressionPercent / (positions.length - 1);
  const averageIncreaseEuro = totalSalaryProgression / (positions.length - 1);
  
  return {
    totalProgression: {
      euro: totalSalaryProgression,
      percent: totalSalaryProgressionPercent
    },
    marketProgression: {
      euro: totalMarketProgression,
      percent: totalMarketProgressionPercent
    },
    averageIncrease: {
      euro: averageIncreaseEuro,
      percent: averageIncreasePercent
    },
    largestJump,
    smallestJump,
    numberOfLevels: positions.length
  };
}

function LevelDifferences({ 
  sortedPositions,
  shortenPositionName
}) {
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
  const [isParametersExpanded, setIsParametersExpanded] = useState(true);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  
  // Filter states
  const [comparisonFilter, setComparisonFilter] = useState('all'); // 'all', 'adjacent', 'custom'
  const [visibleParameters, setVisibleParameters] = useState({
    communicationFrequency: true,
    communicationStyle: true,
    strategicInfluence: true,
    resultVisibility: true
  });
  const [visibleSources, setVisibleSources] = useState({
    compensationPartner: true,
    kununu: true,
    gehaltDe: true
  });
  
  // Comprehensive validation
  if (!sortedPositions || !Array.isArray(sortedPositions)) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Keine Positionsdaten verfügbar</p>
      </div>
    );
  }
  
  if (sortedPositions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Keine Positionen in dieser Abteilung</p>
      </div>
    );
  }
  
  if (sortedPositions.length <= 1) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Wähle mindestens zwei Positionen aus, um Unterschiede zu sehen.</p>
      </div>
    );
  }
  
  // Gruppiere die Positionen nach Department
  const positionsByDepartment = {};
  sortedPositions.forEach(pos => {
    if (!positionsByDepartment[pos.department]) {
      positionsByDepartment[pos.department] = [];
    }
    positionsByDepartment[pos.department].push(pos);
  });
  
  return (
    <div>
      {Object.entries(positionsByDepartment).map(([department, positions]) => {
        const progressionMetrics = calculateCareerProgressionMetrics(positions);
        
        return (
          <div key={department} className="mb-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">{department}</h3>
              <button
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
              >
                <span className="mr-1">🔧</span>
                Filter & Optionen
                <svg 
                  className={`w-4 h-4 ml-1 transition-transform duration-200 ${isFiltersExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {/* Filter Controls */}
            {isFiltersExpanded && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Comparison Filter */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Vergleichs-Modus</div>
                    <select 
                      value={comparisonFilter} 
                      onChange={(e) => setComparisonFilter(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    >
                      <option value="all">Alle Level-Übergänge</option>
                      <option value="adjacent">Nur benachbarte Level</option>
                    </select>
                  </div>
                  
                  {/* Parameter Visibility */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Sichtbare Parameter</div>
                    <div className="space-y-1">
                      {Object.entries({
                        communicationFrequency: 'Kommunikations-Umfang',
                        communicationStyle: 'Kommunikations-Art',
                        strategicInfluence: 'Strategischer Einfluss',
                        resultVisibility: 'Ergebnisse Sichtbarkeit'
                      }).map(([key, label]) => (
                        <label key={key} className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={visibleParameters[key]}
                            onChange={(e) => setVisibleParameters(prev => ({
                              ...prev,
                              [key]: e.target.checked
                            }))}
                            className="mr-2"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Source Visibility */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Markt-Quellen</div>
                    <div className="space-y-1">
                      {Object.entries({
                        compensationPartner: 'Compensation Partner',
                        kununu: 'Kununu',
                        gehaltDe: 'Gehalt.de'
                      }).map(([key, label]) => (
                        <label key={key} className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={visibleSources[key]}
                            onChange={(e) => setVisibleSources(prev => ({
                              ...prev,
                              [key]: e.target.checked
                            }))}
                            className="mr-2"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Quick Reset */}
                <div className="mt-4 pt-3 border-t border-gray-300">
                  <button
                    onClick={() => {
                      setComparisonFilter('all');
                      setVisibleParameters({
                        communicationFrequency: true,
                        communicationStyle: true,
                        strategicInfluence: true,
                        resultVisibility: true
                      });
                      setVisibleSources({
                        compensationPartner: true,
                        kununu: true,
                        gehaltDe: true
                      });
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    🔄 Alle Filter zurücksetzen
                  </button>
                </div>
              </div>
            )}
            
            {/* Career Progression Overview */}
            {progressionMetrics && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow mb-6 overflow-hidden">
                <button
                  onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                  className="w-full p-4 flex items-center justify-between hover:bg-blue-100/30 transition-colors"
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📈</span>
                    <h4 className="text-lg font-semibold">Karriere-Progression Übersicht</h4>
                  </div>
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${isOverviewExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isOverviewExpanded && (
                  <div className="p-6 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* Total Progression Card */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">Karriere-Spanne</div>
                        <div className="text-2xl font-bold text-blue-600">
                          +{progressionMetrics.totalProgression.percent.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          +{progressionMetrics.totalProgression.euro.toLocaleString()} €/Jahr
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          {shortenPositionName(positions[0].positionsBezeichnung)} → {shortenPositionName(positions.at(-1).positionsBezeichnung)}
                        </div>
                      </div>
                      
                      {/* Average per Level Card */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">Ø pro Level</div>
                        <div className="text-2xl font-bold text-green-600">
                          +{progressionMetrics.averageIncrease.percent.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          +{Math.round(progressionMetrics.averageIncrease.euro).toLocaleString()} €/Jahr
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          über {progressionMetrics.numberOfLevels - 1} Stufen
                        </div>
                      </div>
                      
                      {/* Largest Jump Card */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">Größter Sprung</div>
                        <div className="text-2xl font-bold text-indigo-600">
                          +{progressionMetrics.largestJump.percentIncrease.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          +{progressionMetrics.largestJump.euroIncrease.toLocaleString()} €/Jahr
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          {shortenPositionName(progressionMetrics.largestJump.from)} → {shortenPositionName(progressionMetrics.largestJump.to)}
                        </div>
                      </div>
                      
                      {/* Market Alignment Card */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">Markt-Alignment</div>
                        <div className={`text-2xl font-bold ${
                          Math.abs(progressionMetrics.totalProgression.percent - progressionMetrics.marketProgression.percent) < 5 
                            ? 'text-green-600' 
                            : Math.abs(progressionMetrics.totalProgression.percent - progressionMetrics.marketProgression.percent) < 10
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}>
                          {progressionMetrics.totalProgression.percent > progressionMetrics.marketProgression.percent ? '+' : ''}
                          {(progressionMetrics.totalProgression.percent - progressionMetrics.marketProgression.percent).toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          vs. Markt-Progression
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          Markt: +{progressionMetrics.marketProgression.percent.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Visual progression indicator */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-sm font-medium text-gray-700 mb-3">Karriere-Pfad (basierend auf aktuellen Gehältern)</div>
                      <div className="flex items-center justify-between">
                        {positions.map((position, index) => (
                          <div key={position.positionsBezeichnung} className="flex items-center">
                            <div className="text-center">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                                index === 0 ? 'bg-blue-500' : 
                                index === positions.length - 1 ? 'bg-indigo-600' : 
                                'bg-blue-400'
                              }`}>
                                {index + 1}
                              </div>
                              <div className="text-xs mt-1 max-w-[80px]">
                                {shortenPositionName(position.positionsBezeichnung)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {Math.round(position.salary.peerigonCurrentBasic / 1000)}k €
                              </div>
                            </div>
                            {index < positions.length - 1 && (
                              <div className="mx-2 flex items-center">
                                <div className="h-1 w-12 bg-gradient-to-r from-blue-400 to-blue-500"></div>
                                <span className="text-xs text-gray-500 mx-1">
                                  +{((positions[index + 1].salary.peerigonCurrentBasic / position.salary.peerigonCurrentBasic - 1) * 100).toFixed(0)}%
                                </span>
                                <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-blue-400"></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Parameter Evolution Visualization */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow mb-6 overflow-hidden">
              <button
                onClick={() => setIsParametersExpanded(!isParametersExpanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-green-100/30 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🎯</span>
                  <h4 className="text-lg font-semibold">Parameter-Evolution</h4>
                </div>
                <svg 
                  className={`w-5 h-5 transition-transform duration-200 ${isParametersExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isParametersExpanded && (
                <div className="p-6 pt-2">
                  {/* Communication Evolution */}
                  {(visibleParameters.communicationFrequency || visibleParameters.communicationStyle) && (
                    <div className="mb-6">
                      <h5 className="text-md font-medium mb-3 text-gray-700">💬 Kommunikation</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Frequency Evolution */}
                        {visibleParameters.communicationFrequency && (
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-3">Umfang</div>
                        <div className="space-y-2">
                          {positions.map((position, index) => {
                            const level = getCommunicationFrequencyLevel(position.kommunikation);
                            const prevLevel = index > 0 ? getCommunicationFrequencyLevel(positions[index - 1].kommunikation) : null;
                            const hasChanged = prevLevel !== null && level !== prevLevel;
                            
                            return (
                              <div key={position.positionsBezeichnung} className="flex items-center">
                                <div className="w-24 text-xs text-gray-600 truncate">
                                  {shortenPositionName(position.positionsBezeichnung)}
                                </div>
                                <div className="flex-grow mx-2">
                                  <RangeIndicator 
                                    level={level + 1} 
                                    maxLevel={4} 
                                    labels={LEVEL_LABELS.communication.frequency}
                                  />
                                </div>
                                <div className="w-20 text-right">
                                  {hasChanged && (
                                    <span className={`text-xs font-medium ${level > prevLevel ? 'text-green-600' : 'text-orange-600'}`}>
                                      {level > prevLevel ? '↗️ +' : '↘️ '}{Math.abs(level - prevLevel)} Level
                                    </span>
                                  )}
                                  {index > 0 && !hasChanged && (
                                    <span className="text-xs text-gray-400">→ gleich</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                        )}
                      
                      {/* Style Evolution */}
                      {visibleParameters.communicationStyle && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="text-sm text-gray-600 mb-3">Art</div>
                        <div className="space-y-2">
                          {positions.map((position, index) => {
                            const level = getCommunicationStyleLevel(position.kommunikation);
                            const prevLevel = index > 0 ? getCommunicationStyleLevel(positions[index - 1].kommunikation) : null;
                            const hasChanged = prevLevel !== null && level !== prevLevel;
                            
                            return (
                              <div key={position.positionsBezeichnung} className="flex items-center">
                                <div className="w-24 text-xs text-gray-600 truncate">
                                  {shortenPositionName(position.positionsBezeichnung)}
                                </div>
                                <div className="flex-grow mx-2">
                                  <RangeIndicator 
                                    level={level + 1} 
                                    maxLevel={3} 
                                    labels={LEVEL_LABELS.communication.style}
                                  />
                                </div>
                                <div className="w-20 text-right">
                                  {hasChanged && (
                                    <span className={`text-xs font-medium ${level > prevLevel ? 'text-green-600' : 'text-orange-600'}`}>
                                      {level > prevLevel ? '↗️ +' : '↘️ '}{Math.abs(level - prevLevel)} Level
                                    </span>
                                  )}
                                  {index > 0 && !hasChanged && (
                                    <span className="text-xs text-gray-400">→ gleich</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Complexity Evolution */}
                  {(visibleParameters.strategicInfluence || visibleParameters.resultVisibility) && (
                    <div className="mb-6">
                      <h5 className="text-md font-medium mb-3 text-gray-700">🧠 Komplexität</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Strategic Influence Evolution */}
                        {visibleParameters.strategicInfluence && (
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="text-sm text-gray-600 mb-3">Strategischer Einfluss</div>
                        <div className="space-y-2">
                          {positions.map((position, index) => {
                            const level = getStrategicInfluenceLevel(position.komplexität);
                            const prevLevel = index > 0 ? getStrategicInfluenceLevel(positions[index - 1].komplexität) : null;
                            const hasChanged = prevLevel !== null && level !== prevLevel;
                            
                            return (
                              <div key={position.positionsBezeichnung} className="flex items-center">
                                <div className="w-24 text-xs text-gray-600 truncate">
                                  {shortenPositionName(position.positionsBezeichnung)}
                                </div>
                                <div className="flex-grow mx-2">
                                  <RangeIndicator 
                                    level={level + 1} 
                                    maxLevel={6} 
                                    labels={LEVEL_LABELS.complexity.strategic}
                                  />
                                </div>
                                <div className="w-20 text-right">
                                  {hasChanged && (
                                    <span className={`text-xs font-medium ${level > prevLevel ? 'text-green-600' : 'text-orange-600'}`}>
                                      {level > prevLevel ? '↗️ +' : '↘️ '}{Math.abs(level - prevLevel)} Level
                                    </span>
                                  )}
                                  {index > 0 && !hasChanged && (
                                    <span className="text-xs text-gray-400">→ gleich</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                        )}
                      
                      {/* Result Visibility Evolution */}
                      {visibleParameters.resultVisibility && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="text-sm text-gray-600 mb-3">Ergebnisse sichtbar</div>
                        <div className="space-y-2">
                          {positions.map((position, index) => {
                            const level = getResultVisibilityLevel(position.komplexität);
                            const prevLevel = index > 0 ? getResultVisibilityLevel(positions[index - 1].komplexität) : null;
                            const hasChanged = prevLevel !== null && level !== prevLevel;
                            
                            return (
                              <div key={position.positionsBezeichnung} className="flex items-center">
                                <div className="w-24 text-xs text-gray-600 truncate">
                                  {shortenPositionName(position.positionsBezeichnung)}
                                </div>
                                <div className="flex-grow mx-2">
                                  <RangeIndicator 
                                    level={level + 1} 
                                    maxLevel={6} 
                                    labels={LEVEL_LABELS.complexity.visibility}
                                  />
                                </div>
                                <div className="w-20 text-right">
                                  {hasChanged && (
                                    <span className={`text-xs font-medium ${level > prevLevel ? 'text-green-600' : 'text-orange-600'}`}>
                                      {level > prevLevel ? '↗️ +' : '↘️ '}{Math.abs(level - prevLevel)} Level
                                    </span>
                                  )}
                                  {index > 0 && !hasChanged && (
                                    <span className="text-xs text-gray-400">→ gleich</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Summary Insights */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium text-gray-700 mb-2">📊 Zusammenfassung</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Größte Veränderung:</span>
                        <div className="font-medium text-indigo-600">
                          {(() => {
                            let maxChange = { param: '', change: 0, from: '', to: '' };
                            for (let i = 1; i < positions.length; i++) {
                              const changes = [
                                { 
                                  param: 'Kommunikation-Umfang', 
                                  change: Math.abs(getCommunicationFrequencyLevel(positions[i].kommunikation) - getCommunicationFrequencyLevel(positions[i-1].kommunikation))
                                },
                                { 
                                  param: 'Strategischer Einfluss', 
                                  change: Math.abs(getStrategicInfluenceLevel(positions[i].komplexität) - getStrategicInfluenceLevel(positions[i-1].komplexität))
                                },
                              ];
                              const biggest = changes.reduce((max, curr) => curr.change > max.change ? curr : max);
                              if (biggest.change > maxChange.change) {
                                maxChange = { 
                                  ...biggest, 
                                  from: shortenPositionName(positions[i-1].positionsBezeichnung), 
                                  to: shortenPositionName(positions[i].positionsBezeichnung) 
                                };
                              }
                            }
                            return maxChange.change > 0 ? `${maxChange.param} (${maxChange.from} → ${maxChange.to})` : 'Keine signifikanten Änderungen';
                          })()}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Stabilste Parameter:</span>
                        <div className="font-medium text-green-600">
                          {(() => {
                            const params = ['Kommunikation-Art', 'Ergebnisse sichtbar'];
                            // This is simplified - you could add more sophisticated logic
                            return params.join(', ');
                          })()}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Progressions-Typ:</span>
                        <div className="font-medium text-blue-600">
                          {positions.length >= 3 ? 'Stufenweise Entwicklung' : 'Direkter Übergang'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          
          {/* Market Alignment Analysis */}
          <MarketAlignmentAnalysis 
            positions={positions}
            shortenPositionName={shortenPositionName}
            visibleSources={visibleSources}
            className="mt-6"
          />
        </div>
        );
      })}
    </div>
  );
}

export default memo(LevelDifferences);